import React from 'react';
import { ModalType } from '../types';

interface ClarityProps {
  onOpenModal: (type: ModalType) => void;
}

const Clarity: React.FC<ClarityProps> = ({ onOpenModal }) => (
  <section
    id="clarity"
    className="relative py-[120px] px-10"
    style={{ background: 'linear-gradient(185deg, #080807 0%, #131210 100%)' }}
  >
    <div className="max-w-[1200px] mx-auto relative z-10">
      {/* Header */}
      <div className="text-center mb-[72px]">
        <div className="flex items-center justify-center gap-[14px] mb-5" style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.32em', textTransform: 'uppercase', color: '#b89a5a', fontFamily: 'Montserrat, sans-serif' }}>
          <span style={{ width: 28, height: 1, background: '#b89a5a', display: 'inline-block' }} />
          Start Here
        </div>
        <h2 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 'clamp(34px, 4.5vw, 58px)', fontWeight: 300, lineHeight: 1.1, color: '#f2ece0', textAlign: 'center' }}>
          Start With <em style={{ fontStyle: 'italic', color: '#b89a5a' }}>Clarity.</em>
        </h2>
        <p style={{ fontSize: 12, fontWeight: 300, color: '#78736c', maxWidth: 520, margin: '16px auto 0', lineHeight: 2, letterSpacing: '0.02em', textAlign: 'center' }}>
          Before strategy, before proposals, before anything else — we need to understand exactly where your brand stands. Submit the diagnostic that fits your situation. If there is a strategic fit, we'll review it and outline the next step.
        </p>
      </div>

      <div
        className="grid grid-cols-1 md:grid-cols-2 max-w-[960px] mx-auto"
        style={{ gap: 1, background: 'rgba(184,154,90,0.1)', border: '1px solid rgba(184,154,90,0.1)' }}
      >
        {/* Marketing Diagnostic */}
        <div className="fade-up flex flex-col" style={{ background: '#0e0d0b', padding: '56px 52px' }}>
          <div className="flex items-center gap-[10px] mb-5" style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#b89a5a', fontFamily: 'Montserrat, sans-serif' }}>
            <span style={{ width: 20, height: 1, background: '#b89a5a', display: 'inline-block' }} />
            Marketing Diagnostic
          </div>
          <h3 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 26, fontWeight: 400, color: '#f2ece0', marginBottom: 16, lineHeight: 1.2 }}>
            Analyze My<br />Marketing
          </h3>
          <p style={{ fontSize: 12, fontWeight: 300, color: '#78736c', lineHeight: 2.1, marginBottom: 16, letterSpacing: '0.02em' }}>
            For brands whose marketing is active but not producing consistent, compounding growth. If your content is going out but not converting — or your paid ads are spending but not scaling — this is where you start.
          </p>
          <p style={{ fontSize: 11, fontWeight: 500, color: '#f2ece0', letterSpacing: '0.04em', marginBottom: 8 }}>Choose this if your challenge is:</p>
          <p style={{ fontSize: 12, fontWeight: 300, color: '#78736c', lineHeight: 2.1, marginBottom: 16, letterSpacing: '0.02em' }}>
            Visibility, content performance, brand positioning, platform strategy, or paid acquisition. You know marketing is happening — you need to understand why it isn't working at the level it should.
          </p>
          <div style={{ fontSize: 11, color: '#78736c', marginBottom: 36, paddingTop: 20, borderTop: '1px solid rgba(184,154,90,0.08)', lineHeight: 1.8 }}>
            <strong style={{ color: '#f2ece0', fontWeight: 500 }}>What happens next:</strong> Submit the form and our team reviews your submission within 48 hours. If there's a strategic fit, we'll reach out to schedule a structured diagnostic conversation.
          </div>
          <button
            onClick={() => onOpenModal('marketing')}
            className="border-none cursor-pointer transition-all duration-200 mt-auto"
            style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase', background: '#b89a5a', color: '#0d0c0b', padding: '16px 36px', alignSelf: 'flex-start' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#d4b87a'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#b89a5a'; }}
          >
            Analyze My Marketing
          </button>
        </div>

        {/* Operations Diagnostic */}
        <div className="fade-up flex flex-col" style={{ background: '#0e0d0b', padding: '56px 52px' }}>
          <div className="flex items-center gap-[10px] mb-5" style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#b89a5a', fontFamily: 'Montserrat, sans-serif' }}>
            <span style={{ width: 20, height: 1, background: '#b89a5a', display: 'inline-block' }} />
            Operations Diagnostic
          </div>
          <h3 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontSize: 26, fontWeight: 400, color: '#f2ece0', marginBottom: 16, lineHeight: 1.2 }}>
            Fix My<br />Operation System
          </h3>
          <p style={{ fontSize: 12, fontWeight: 300, color: '#78736c', lineHeight: 2.1, marginBottom: 16, letterSpacing: '0.02em' }}>
            For brands whose backend is fragmented, slow, or broken. If your follow-up is manual, your client journey is inconsistent, your workflows are chaotic, or you don't have the operational infrastructure to support growth — this is where you start.
          </p>
          <p style={{ fontSize: 11, fontWeight: 500, color: '#f2ece0', letterSpacing: '0.04em', marginBottom: 8 }}>Choose this if your challenge is:</p>
          <p style={{ fontSize: 12, fontWeight: 300, color: '#78736c', lineHeight: 2.1, marginBottom: 16, letterSpacing: '0.02em' }}>
            CRM setup, workflow design, team operations, client onboarding structure, follow-up systems, or operational execution. The front-end looks fine — the backend is what's holding you back.
          </p>
          <div style={{ fontSize: 11, color: '#78736c', marginBottom: 36, paddingTop: 20, borderTop: '1px solid rgba(184,154,90,0.08)', lineHeight: 1.8 }}>
            <strong style={{ color: '#f2ece0', fontWeight: 500 }}>What happens next:</strong> Submit the form and our team reviews your submission within 48 hours. If there's a strategic fit, we'll reach out to schedule a structured diagnostic conversation.
          </div>
          <button
            onClick={() => onOpenModal('operations')}
            className="border-none cursor-pointer transition-all duration-200 mt-auto"
            style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.22em', textTransform: 'uppercase', background: '#b89a5a', color: '#0d0c0b', padding: '16px 36px', alignSelf: 'flex-start' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#d4b87a'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#b89a5a'; }}
          >
            Fix My Operation System
          </button>
        </div>
      </div>
    </div>
  </section>
);

export default Clarity;
